/**
 * app-status-response.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { validateAs, validateFor } from "../../validation/decorators";
import { IncomingMessage } from "../general/incoming-message";
import { MessageTag } from "../message-tag";

/**
 * The {@link AppStatusResponse} provides details about the overall application
 * status, including administrative states and operations, as well as general
 * resource usage and system health. This data is consumed by the admin panel.
 * Note that statically configured values are handled instead by {@link
 * AppConfigurationResponse}.
 */
@validateFor(MessageTag.RSP_STATUS_APP)
export class AppStatusResponse extends IncomingMessage
{
    public tag : MessageTag = MessageTag.RSP_STATUS_APP;

    /**
     * Whether the app has debug mode enabled.
     */
    @validateAs("boolean")
    public debugMode : boolean;

    /**
     * Whether the app has generated code debugging enabled.
     */
    @validateAs("boolean")
    public codeGenDebugMode : boolean;

    /**
     * Whether the app is currently locked.
     */
    @validateAs("boolean")
    public administrativelyLocked : boolean;

    /**
     * Number of seconds until administrative lock. This value is negative if no
     * lock is pending.
     */
    @validateAs("integer")
    public timeUntilAdministrativelyLocked : number;

    /**
     * Last admin to request a lock, or empty if no lock is pending or active.
     */
    @validateAs("string")
    public adminRequestingLock : string;

    /**
     * Seconds until the application is shut down, or negative if no shutdown is
     * pending.
     */
    @validateAs("integer")
    public timeUntilShutdown : number;

    /**
     * Last admin to request shutdown, or empty if no pending shutdown.
     */
    @validateAs("string")
    public adminRequestingShutdown : string;

    /**
     * Whether the database is currently connected.
     */
    @validateAs("boolean")
    public databaseConnected : boolean;

    /**
     * Number of currently authenticated channels.
     */
    @validateAs(["integer", {min: 0}])
    public activeAuthenticatedChannelCount : number;

    /**
     * Number of distinct, currently authenticated users.
     */
    @validateAs(["integer", {min: 0}])
    public loggedInUserCount : number;

    /**
     * Number of current unauthenticated channels.
     */
    @validateAs(["integer", {min: 0}])
    public unauthenticatedActiveChannelCount : number;

    @validateAs("integer")
    public startTime : number;

    /**
     * Counts of the various application cache contents.
     */
    @validateAs({array: "object"}) // TODO better validation
    public caches : Array<{
        cache : string;
        strongCacheCount : number;
        softCacheCount : number;
        providerQueueCount : number;
    }>;
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/messages/admin/app-status-response.ts